import QRCode from "qrcode";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/signage.css";
import "./styles/interactive.css";
import { categories, dishes, scenarios } from "./config/content.config";
import { restaurantConfig } from "./config/restaurant.config";
import { scheduleConfig } from "./config/schedule.config";
import { getScheduleState } from "./domain/schedule";
import type { Category, Dish, ScenarioId, ScheduleResult, Slide } from "./domain/types";
import { IdleTimer } from "./interactive/idle-timer";
import { escapeHtml, pictureMarkup, preloadImage } from "./media/image-loader";
import { track } from "./platform/analytics";
import { buildPlaylist } from "./player/playlist-builder";
import { TimerManager } from "./player/timer-manager";

type Mode = "autoplay" | "interactive";
type Screen = "categories" | "catalog" | "dish" | "restaurant";

const app = document.querySelector<HTMLDivElement>("#app")!;
const query = new URLSearchParams(location.search);
const debug = query.get("debug") === "1";
const forcedScenarioMap: Record<string, ScenarioId> = { morning: "MORNING", lunch: "WEEKDAY_LUNCH", weekend: "WEEKEND_DAY", evening: "EVENING", closed: "CLOSED" };
const forcedScenario = forcedScenarioMap[query.get("scenario") ?? ""];
const forcedOrientation = query.get("orientation");
if (forcedOrientation === "portrait" || forcedOrientation === "landscape") document.documentElement.dataset.orientation = forcedOrientation;

let mode: Mode = "autoplay";
let screen: Screen = "categories";
let scheduleState: ScheduleResult;
let scenarioId: ScenarioId = "FALLBACK";
let playlist: Slide[] = [];
let slideIndex = 0;
let selectedCategory: Category | undefined;
let selectedDish: Dish | undefined;
let activeSubcategory = "Все";
let warningSeconds = 0;
let sessionStartedAt = 0;
let mapQr = "";
let pointerStartX = 0;
const autoplayTimers = new TimerManager();
const lifecycleTimers = new TimerManager();

const idleTimer = new IdleTimer(
  restaurantConfig.interaction.idleTimeoutSeconds,
  restaurantConfig.interaction.warningSeconds,
  (seconds) => { warningSeconds = seconds; renderWarning(); },
  () => exitInteractive(true),
);

function evaluateSchedule(): void {
  scheduleState = getScheduleState(new Date(), restaurantConfig.timezone, scheduleConfig);
  scenarioId = forcedScenario ?? scheduleState.scenario;
  const scenario = scenarios.find((item) => item.id === scenarioId);
  playlist = buildPlaylist(scenario, scheduleState.clock);
  if (!playlist.length && scenarioId !== "CLOSED") scenarioId = "FALLBACK";
}

function startAutoplay(preserveIndex = false): void {
  autoplayTimers.clearAll();
  evaluateSchedule();
  if (!preserveIndex || slideIndex >= playlist.length) slideIndex = 0;
  render();
  if (playlist.length) {
    const current = playlist[slideIndex]!;
    preloadImage(playlist[(slideIndex + 1) % playlist.length]?.imageId);
    autoplayTimers.timeout(() => {
      slideIndex = (slideIndex + 1) % playlist.length;
      startAutoplay(true);
    }, current.durationSeconds * 1000);
  }
  const boundaryDelay = Math.max(250, scheduleState.nextBoundaryAt.getTime() - Date.now() + 50);
  autoplayTimers.timeout(() => startAutoplay(false), boundaryDelay);
}

function enterInteractive(): void {
  if (scenarioId === "CLOSED" && !restaurantConfig.allowInteractiveWhenClosed) return;
  autoplayTimers.clearAll();
  mode = "interactive";
  screen = "categories";
  selectedCategory = undefined;
  selectedDish = undefined;
  sessionStartedAt = Date.now();
  warningSeconds = 0;
  track("touches");
  idleTimer.reset();
  render();
}

function exitInteractive(automatic = false): void {
  if (mode !== "interactive") return;
  idleTimer.stop();
  document.querySelector(".idle-warning")?.remove();
  track("interactiveSeconds", Math.round((Date.now() - sessionStartedAt) / 1000));
  if (automatic) track("automaticReturns");
  mode = "autoplay";
  warningSeconds = 0;
  startAutoplay(false);
}

function touchActivity(): void {
  if (mode === "interactive") {
    warningSeconds = 0;
    document.querySelector(".idle-warning")?.remove();
    idleTimer.reset();
  }
}

function price(value?: number): string { return value === undefined ? "" : `${new Intl.NumberFormat("ru-RU").format(value)} ₽`; }

function render(): void {
  document.body.dataset.mode = mode;
  if (mode === "autoplay") renderAutoplay(); else renderInteractive();
  if (debug) renderDebug();
}

function renderAutoplay(): void {
  if (scenarioId === "CLOSED") { renderClosed(); return; }
  const current = playlist[slideIndex];
  if (!current) { renderFallback(); return; }
  const treatment = current.treatment ?? (scenarioId === "EVENING" ? "dark" : "light");
  app.innerHTML = `<main class="signage signage--${treatment}" data-screen="advertising">
    ${current.imageId ? pictureMarkup(current.imageId, current.title, "signage__media") : ""}
    <div class="signage__shade"></div>
    <header class="brand-mark"><span>О</span><strong>ОСЕНЬ</strong><small>кафе на Яузской</small></header>
    <section class="signage__copy">
      ${current.kicker ? `<p class="eyebrow">${escapeHtml(current.kicker)}</p>` : ""}
      <h1>${escapeHtml(current.title)}</h1>
      ${current.description ? `<p class="signage__description">${escapeHtml(current.description)}</p>` : ""}
      ${current.price ? `<p class="signage__price">${price(current.price)}</p>` : ""}
    </section>
    <div class="touch-hint" aria-label="Коснитесь экрана, чтобы выбрать блюдо"><span class="touch-hint__pulse"></span><span><strong>Выберите блюдо</strong><small>Коснитесь — всё меню за минуту</small></span><i>→</i></div>
    <div class="slide-progress">${playlist.map((_, index) => `<i class="${index === slideIndex ? "is-active" : ""}"></i>`).join("")}</div>
  </main>`;
}

function renderClosed(): void {
  const label = scheduleState.nextOpeningLabel ?? "Скоро увидимся";
  app.innerHTML = `<main class="signage signage--closed" data-screen="closed">
    ${pictureMarkup("interior-05", "Интерьер кафе Осень", "signage__media")}
    <div class="signage__shade"></div>
    <header class="brand-mark"><span>О</span><strong>ОСЕНЬ</strong><small>кафе на Яузской</small></header>
    <section class="closed-copy"><p class="eyebrow">До встречи</p><h1>${escapeHtml(label)}</h1><p>Пн–пт 08:30–22:00<br>Сб–вс 10:00–22:00</p></section>
    ${restaurantConfig.allowInteractiveWhenClosed ? `<div class="touch-hint"><span class="touch-hint__pulse"></span><span><strong>Коснитесь экрана</strong><small>Меню доступно для просмотра</small></span></div>` : ""}
  </main>`;
}

function renderFallback(): void {
  app.innerHTML = `<main class="fallback" data-screen="fallback"><div class="brand-mark brand-mark--center"><span>О</span><strong>ОСЕНЬ</strong><small>кафе на Яузской</small></div><p>Сегодня мы рядом до 22:00</p></main>`;
}

function renderInteractive(): void {
  if (screen === "categories") renderCategories();
  if (screen === "catalog") renderCatalog();
  if (screen === "dish") renderDish();
  if (screen === "restaurant") renderRestaurant();
}

function interactiveShell(title: string, eyebrow: string, content: string, back = false): string {
  return `<main class="interactive" data-screen="${screen}">
    <header class="interactive__header">
      ${back ? `<button class="round-button" data-action="back" aria-label="Назад">←</button>` : `<div class="mini-mark">О</div>`}
      <div><p class="eyebrow">${escapeHtml(eyebrow)}</p><h1>${escapeHtml(title)}</h1></div>
      <button class="exit-button" data-action="exit">К рекламе <span>×</span></button>
    </header>
    ${content}
  </main>`;
}

function renderCategories(): void {
  const currentDay = scheduleState.clock.dayOfWeek;
  const cards = categories.filter((category) => category.enabled).map((category) => {
    const lunchUnavailable = category.id === "lunch" && currentDay > 5;
    return `<button class="category-card ${lunchUnavailable ? "is-informational" : ""}" data-category="${category.id}">
      ${pictureMarkup(category.imageId ?? "interior-01", category.name)}
      <span class="category-card__shade"></span>
      <span class="category-card__copy"><small>${escapeHtml(lunchUnavailable ? "По будням · 12:00–17:00" : category.eyebrow)}</small><strong>${escapeHtml(category.name)}</strong><i>→</i></span>
    </button>`;
  }).join("");
  app.innerHTML = interactiveShell("Что хочется сегодня?", "Меню кафе «Осень»", `<section class="category-grid">${cards}</section><footer class="interactive__footer"><span>Завтраки подаём весь день</span><span>Яузская, 8, стр. 2</span></footer>`);
}

function categoryDishes(): Dish[] {
  if (!selectedCategory) return [];
  return dishes
    .filter((dish) => dish.enabled && dish.categoryId === selectedCategory!.id && (activeSubcategory === "Все" || dish.subcategoryId === activeSubcategory))
    .sort((left, right) => Number(Boolean(right.image)) - Number(Boolean(left.image)));
}

function renderCatalog(): void {
  if (!selectedCategory) { screen = "categories"; renderCategories(); return; }
  const all = dishes.filter((dish) => dish.enabled && dish.categoryId === selectedCategory!.id);
  const tabs = ["Все", ...new Set(all.map((dish) => dish.subcategoryId).filter(Boolean) as string[])];
  const tabMarkup = tabs.length > 2 ? `<nav class="filter-tabs">${tabs.map((tab) => `<button class="${tab === activeSubcategory ? "is-active" : ""}" data-filter="${escapeHtml(tab)}">${escapeHtml(tab)}</button>`).join("")}</nav>` : "";
  const cards = categoryDishes().map((dish) => `<button class="dish-card" data-dish="${dish.id}">
    <div class="dish-card__image">${dish.image ? pictureMarkup(dish.image.id, dish.image.alt) : `<div class="image-fallback"><span>О</span></div>`}${dish.badge ? `<span class="badge">${escapeHtml(dish.badge)}</span>` : ""}</div>
    <span class="dish-card__copy"><small>${escapeHtml(dish.subcategoryId ?? selectedCategory!.name)}</small><strong>${escapeHtml(dish.name)}</strong>${dish.description ? `<span>${escapeHtml(dish.description)}</span>` : ""}<b>${price(dish.price) || "Уточните у команды"}</b></span>
  </button>`).join("");
  const empty = `<div class="empty-state"><span>О</span><h2>Раздел обновляется</h2><p>Команда «Осени» уже готовит новые позиции.</p></div>`;
  app.innerHTML = interactiveShell(selectedCategory.name, selectedCategory.eyebrow, `${tabMarkup}<section class="dish-rail">${cards || empty}</section>`, true);
}

function renderDish(): void {
  if (!selectedDish || !selectedCategory) { screen = "catalog"; renderCatalog(); return; }
  const visualId = selectedDish.image?.id ?? selectedCategory.imageId ?? "interior-01";
  const content = `<section class="dish-detail">
    <div class="dish-detail__media">${pictureMarkup(visualId, selectedDish.image?.alt ?? selectedDish.name)}${selectedDish.badge ? `<span class="badge">${escapeHtml(selectedDish.badge)}</span>` : ""}</div>
    <div class="dish-detail__copy"><p class="eyebrow">${escapeHtml(selectedDish.subcategoryId ?? selectedCategory.name)}</p><h2>${escapeHtml(selectedDish.name)}</h2>${selectedDish.description ? `<p>${escapeHtml(selectedDish.description)}</p>` : ""}<strong>${price(selectedDish.price) || "Цена уточняется"}</strong><button class="primary-button" data-action="back">Вернуться к меню</button></div>
  </section>`;
  app.innerHTML = interactiveShell(selectedDish.name, selectedCategory.name, content, true);
}

function renderRestaurant(): void {
  const content = `<section class="restaurant-screen">
    <div class="restaurant-screen__media">${pictureMarkup("interior-01", "Интерьер кафе Осень")}</div>
    <div class="restaurant-screen__copy"><p class="lead">${escapeHtml(restaurantConfig.shortDescription)}</p><dl><div><dt>Адрес</dt><dd>${escapeHtml(restaurantConfig.address)}</dd></div><div><dt>Телефон</dt><dd>${escapeHtml(restaurantConfig.phone)}</dd></div><div><dt>Часы работы</dt><dd>Пн–пт 08:30–22:00<br>Сб–вс 10:00–22:00</dd></div></dl><div class="qr-block">${mapQr ? `<img src="${mapQr}" alt="QR-код для построения маршрута">` : ""}<span><strong>Построить маршрут</strong><small>Наведите камеру телефона</small></span></div></div>
  </section>`;
  app.innerHTML = interactiveShell("Рады видеть вас", "Кафе «Осень»", content, true);
}

function renderWarning(): void {
  if (!restaurantConfig.interaction.showCountdown || warningSeconds <= 0 || mode !== "interactive") return;
  document.querySelector(".idle-warning")?.remove();
  document.body.insertAdjacentHTML("beforeend", `<aside class="idle-warning" role="dialog"><p>Возвращаемся к рекламе через <strong>${warningSeconds}</strong></p><div><button data-action="continue">Продолжить просмотр</button><button data-action="exit">Вернуться сейчас</button></div></aside>`);
}

function renderDebug(): void {
  document.querySelector(".debug-panel")?.remove();
  document.body.insertAdjacentHTML("beforeend", `<aside class="debug-panel"><strong>DEBUG · v${__APP_VERSION__}</strong><span>${restaurantConfig.timezone}: ${String(scheduleState.clock.hour).padStart(2,"0")}:${String(scheduleState.clock.minute).padStart(2,"0")}:${String(scheduleState.clock.second).padStart(2,"0")}</span><span>state: ${scenarioId}${mode === "interactive" ? " / INTERACTIVE" : ""}</span><span>slide: ${playlist[slideIndex]?.id ?? "—"} (${slideIndex + 1}/${playlist.length})</span><span>next: ${scheduleState.nextBoundaryAt.toISOString()}</span><span>orientation: ${matchMedia("(orientation: portrait)").matches ? "portrait" : "landscape"}</span><span>online: ${navigator.onLine}</span></aside>`);
}

app.addEventListener("pointerdown", (event) => {
  pointerStartX = event.clientX;
  if (mode === "autoplay") { enterInteractive(); return; }
  touchActivity();
});

app.addEventListener("pointerup", (event) => {
  if (mode !== "interactive" || screen !== "catalog" || Math.abs(event.clientX - pointerStartX) < 80) return;
  const rail = app.querySelector<HTMLElement>(".dish-rail");
  rail?.scrollBy({ left: event.clientX < pointerStartX ? rail.clientWidth * 0.8 : -rail.clientWidth * 0.8, behavior: "smooth" });
});

document.addEventListener("click", (event) => {
  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action], [data-category], [data-dish], [data-filter]");
  if (!target) return;
  const action = target.dataset.action;
  if (action === "exit") { exitInteractive(false); return; }
  if (action === "continue") { warningSeconds = 0; document.querySelector(".idle-warning")?.remove(); idleTimer.reset(); return; }
  if (action === "back") {
    if (screen === "dish") screen = "catalog"; else screen = "categories";
    selectedDish = undefined; render(); return;
  }
  if (target.dataset.filter) { activeSubcategory = target.dataset.filter; render(); return; }
  if (target.dataset.category) {
    selectedCategory = categories.find((category) => category.id === target.dataset.category);
    if (selectedCategory?.id === "restaurant") screen = "restaurant"; else { screen = "catalog"; activeSubcategory = "Все"; track("categories"); }
    render(); return;
  }
  if (target.dataset.dish) { selectedDish = dishes.find((dish) => dish.id === target.dataset.dish); screen = "dish"; track("dishes"); render(); }
});

app.addEventListener("error", (event) => {
  const image = event.target as HTMLImageElement;
  if (image.tagName !== "IMG") return;
  track("imageErrors");
  const picture = image.closest("picture");
  if (picture) picture.outerHTML = `<div class="image-fallback"><span>О</span></div>`;
}, true);

for (const event of ["visibilitychange", "pageshow", "online"]) window.addEventListener(event, () => {
  if (document.visibilityState === "hidden") return;
  if (mode === "autoplay") startAutoplay(false); else evaluateSchedule();
});
window.addEventListener("orientationchange", () => render());
window.addEventListener("contextmenu", (event) => event.preventDefault());
lifecycleTimers.interval(() => { if (mode === "autoplay") startAutoplay(true); else evaluateSchedule(); }, 30_000);
lifecycleTimers.timeout(() => location.reload(), 24 * 60 * 60 * 1000);

async function init(): Promise<void> {
  mapQr = await QRCode.toDataURL(restaurantConfig.mapUrl, { width: 280, margin: 1, color: { dark: "#241812", light: "#fffaf0" } });
  startAutoplay();
  if ("serviceWorker" in navigator && import.meta.env.PROD) navigator.serviceWorker.register("./sw.js").catch(() => undefined);
}

void init();
